import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

declare module 'react-i18next' {
  interface CustomTypeOptions {
    resources: {
      translation: typeof resources.en.translation;
    };
  }
}

declare module 'i18next' {
  interface TFunction {
    (key: string): string;
  }
}

const resources = {
  en: {
    translation: {
      nav: {
        intro: 'Introduction',
        insurance: 'Life & Critical Illness',
        calculator: 'Travel / Visitor Insurance',
        contact: 'Contact'
      },
      intro: {
        name: 'Tian Wei Wei',
        title: 'Senior Financial Advisor',
        description: 'With over 10 years of experience in financial planning and insurance.',
        company: 'ABC Financial Services Ltd.'
      },
      wizard: {
        title: 'Which Insurance Do I Need?',
        subtitle: 'Answer a few quick questions to see which type of life or critical illness coverage may fit you.',
        common: {
          yes: 'Yes',
          no: 'No'
        },
        profile: {
          title: 'Your basic profile',
          ageRange: 'Age range',
          under35: 'Under 35',
          '35to55': '35 - 55',
          '55plus': '55+',
          familyStatus: 'Family status',
          single: 'Single',
          withFamily: 'With family / dependents',
          dependents: 'Do you have financial dependents?'
        },
        goals: {
          title: 'Your main protection goal',
          incomeProtection: 'Protect family income',
          mortgageProtection: 'Protect mortgage / major loans',
          criticalIllness: 'Cover critical illness risk'
        },
        budget: {
          title: 'Monthly budget for insurance',
          limited: 'Limited budget',
          medium: 'Moderate budget',
          flexible: 'Flexible budget'
        },
        result: {
          title: 'Suggested direction',
          disclaimer: 'This is a general suggestion only. We will tailor a detailed plan based on your full situation.'
        },
        recommend: {
          familyProtection: 'Based on your answers, a term life plan with sufficient coverage to replace several years of income, plus optional critical illness protection, may be suitable.',
          mortgageProtection: 'Based on your answers, a term life plan aligned with your remaining mortgage period is often a good starting point.',
          criticalIllness: 'Based on your answers, a critical illness plan that provides a lump-sum benefit on diagnosis may be appropriate.',
          budgetFriendly: 'Given a more limited budget, starting with essential term life coverage and gradually adding benefits over time can be an effective approach.',
          seniorFocus: 'For clients over 55, it is important to carefully balance coverage needs and premium levels; a focused protection plan with clear priorities is recommended.',
          general: 'A balanced combination of life insurance and critical illness coverage can help protect your family, income, and long-term goals.'
        },
        actions: {
          back: 'Back',
          next: 'Next',
          restart: 'Start again'
        }
      },
      calculator: {
        title: 'Travel Insurance Calculator',
        age: 'Age',
        gender: 'Gender',
        male: 'Male',
        female: 'Female',
        coverageAmount: 'Coverage Amount',
        insuranceType: 'Insurance Type',
        term: 'Term',
        whole: 'Whole Life',
        universal: 'Universal Life',
        smokingStatus: 'Smoking Status',
        no: 'No',
        yes: 'Yes',
        calculate: 'Calculate Premium',
        estimatedPremium: 'Estimated Monthly Premium',
        disclaimer: 'This is an estimate only.'
      },
      insurance: {
        title: 'Our Insurance Products',
        learnMore: 'Learn More',
        product1: {
          name: 'Term Life Insurance',
          description: 'Affordable protection for a specific period.',
          feature1: 'Flexible terms',
          feature2: 'Fixed premiums',
          feature3: 'High coverage amounts'
        }
      },
      travelInsurance: {
        title: 'Travel & Visitor Insurance Plans',
        learnMore: 'Learn More',
        product1: {
          name: 'Visitor Medical Insurance',
          description: 'Medical coverage for visitors during their stay abroad.',
          feature1: 'Emergency medical coverage',
          feature2: 'Hospital and doctor visits',
          feature3: 'Optional trip interruption benefits'
        }
      },
      contact: {
        title: 'Contact Me',
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        wechat: 'WeChat',
        message: 'Message',
        submit: 'Send Message',
        submitting: 'Submitting...',
        successMessage: 'Thank you for your message!',
        successFollowup: 'We will contact you within 1 business day.',
        throttledMessage: 'We have received your message, will contact you within 1 working day.',
        errorMessage: 'Failed to send message. Please try again later.'
      }
    }
  },
  zh: {
    translation: {
      nav: {
        intro: '介绍',
        insurance: '寿险 / 重疾险',
        calculator: '旅游 / 访客保险',
        contact: '联系我们'
      },
      intro: {
        name: '田惟玮',
        title: '高级理财顾问',
        description: '拥有超过10年的财务规划和保险经验。',
        company: 'ABC金融服务有限公司'
      },
      wizard: {
        title: '我需要哪种保险？',
        subtitle: '通过几个简单问题，帮助您初步了解适合的寿险或重疾险方向。',
        common: {
          yes: '是',
          no: '否'
        },
        profile: {
          title: '您的基本情况',
          ageRange: '年龄区间',
          under35: '35岁以下',
          '35to55': '35-55岁',
          '55plus': '55岁以上',
          familyStatus: '家庭状况',
          single: '单身',
          withFamily: '有家庭 / 有经济依赖人',
          dependents: '是否有需要您经济支持的家人？'
        },
        goals: {
          title: '您的主要保障目标',
          incomeProtection: '保障家庭收入',
          mortgageProtection: '保障房贷 / 大额贷款',
          criticalIllness: '重大疾病保障'
        },
        budget: {
          title: '您每月可接受的保费区间',
          limited: '预算较紧张',
          medium: '预算适中',
          flexible: '预算较灵活'
        },
        result: {
          title: '初步建议方向',
          disclaimer: '以上仅为一般性建议，具体方案需根据您完整的家庭与财务情况进行专业规划。'
        },
        recommend: {
          familyProtection: '根据您的回答，一份能覆盖多年家庭收入的定期寿险方案，并可考虑搭配一定额度的重疾保障，可能比较适合您。',
          mortgageProtection: '根据您的回答，一份与房贷剩余年限匹配的定期寿险，通常是不错的起点选择。',
          criticalIllness: '根据您的回答，一份在确诊重大疾病时可一次性给付的重疾险方案，可能更符合您的需求。',
          budgetFriendly: '在预算相对有限的情况下，可先从基础定期寿险做起，后续视情况逐步增加保障内容与额度。',
          seniorFocus: '对于55岁以上的客户，更需要在保障与保费之间做精细平衡，可重点聚焦最重要的风险与目标。',
          general: '综合考虑，大多数家庭适合在寿险与重疾险之间做合理搭配，以同时守护家庭收入、生活品质与长期目标。'
        },
        actions: {
          back: '上一步',
          next: '下一步',
          restart: '重新开始'
        }
      },
      insurance: {
        title: '我们的保险产品',
        learnMore: '了解更多',
        product1: {
          name: '定期寿险',
          description: '在约定年限内提供较高保额的经济型保障方案。',
          feature1: '保障期间灵活可选',
          feature2: '保费相对固定、性价比高',
          feature3: '可为家庭提供较高身故保障额度'
        }
      },
      calculator: {
        title: '旅游保险保费计算器',
        age: '年龄',
        gender: '性别',
        male: '男',
        female: '女',
        coverageAmount: '保额',
        insuranceType: '保险类型',
        term: '定期寿险',
        whole: '终身寿险',
        universal: '万能寿险',
        smokingStatus: '是否吸烟',
        no: '否',
        yes: '是',
        calculate: '计算保费',
        estimatedPremium: '预估月保费',
        disclaimer: '结果仅供参考，实际保费以保险公司核保为准。'
      },
      travelInsurance: {
        title: '旅游 / 访客保险产品',
        learnMore: '了解更多',
        product1: {
          name: '访客医疗保险',
          description: '为来访短期停留人士提供医疗保障。',
          feature1: '紧急医疗费用保障',
          feature2: '住院与门诊费用报销',
          feature3: '可选行程中断等额外保障'
        }
      },
      contact: {
        title: '联系我',
        name: '姓名',
        email: '邮箱',
        phone: '电话',
        wechat: '微信',
        message: '消息',
        submit: '发送消息',
        submitting: '提交中...',
        successMessage: '感谢您的留言！',
        successFollowup: '我们会在1个工作日内与您联系。',
        throttledMessage: '我们已经收到您的留言，会在1个工作日内与您联系。',
        errorMessage: '消息发送失败，请稍后重试。'
      }
    }
  }
};

i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    resources,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    }
  });

export default i18n;
